import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Brand from "@/components/Brand";

// Publicly accessible Terms & Conditions. Deep-linked from Login, Profile
// footer, and Play Store listing. Copy is India-jurisdiction and tailored
// to StyleBay (AI styling, wardrobe uploads, Razorpay payments, subscriptions).

export default function Terms() {
  return (
    <div
      className="min-h-screen bg-background text-foreground"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="terms-page"
    >
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/login" data-testid="terms-back-btn" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em]">
            <ArrowLeft size={14} /> Back
          </Link>
          <Brand size="sm" />
          <span className="w-16" />
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-10 lg:py-16">
        <p className="eyebrow">Legal</p>
        <h1 className="editorial-h1 mt-3">Terms &amp; Conditions</h1>
        <p className="text-xs text-muted-foreground mt-3">Last updated: 12 February 2026</p>

        <div className="prose prose-sm dark:prose-invert mt-8 space-y-6">
          <Section title="1. Acceptance of Terms">
            By downloading, installing, or using the StyleBay mobile or web application (the “Service”),
            operated by StyleBay (“we”, “us”, “our”), you agree to be bound by these Terms &amp; Conditions
            (the “Terms”). If you do not agree, do not use the Service.
          </Section>

          <Section title="2. Eligibility">
            You must be at least 13 years of age to use the Service. If you are under 18, you must have
            parental or guardian consent. By using the Service you represent that you meet these
            requirements.
          </Section>

          <Section title="3. Account &amp; Google Sign-In">
            StyleBay uses Google Sign-In as the sole authentication method. When you sign in you authorise
            us to receive your name, email address and profile picture from Google. You are responsible for
            maintaining the confidentiality of your Google account credentials and for any activity that
            occurs under your account.
          </Section>

          <Section title="4. AI-Generated Content">
            The Service uses artificial intelligence models (including but not limited to Google Gemini,
            Anthropic Claude and OpenAI) to generate styling recommendations, virtual try-ons, hair/beard/
            makeup previews and shopping suggestions. AI output is provided “as-is”, may contain errors,
            and should not be treated as professional advice (medical, legal, financial or otherwise).
          </Section>

          <Section title="5. Uploaded Content">
            You retain ownership of photos and other content you upload (“User Content”). By uploading,
            you grant StyleBay a worldwide, royalty-free, non-exclusive licence to store, process, transmit,
            display and use your User Content solely to operate and improve the Service. You represent
            that you have all necessary rights to upload such content and that it does not violate any
            third-party rights or applicable law.
          </Section>

          <Section title="6. Subscriptions &amp; Payments">
            Paid plans (Premium Monthly, Premium Annual, Day/Travel passes) are billed via Razorpay in
            Indian Rupees (INR). Prices are inclusive of applicable GST unless stated otherwise.
            Subscriptions auto-renew until cancelled from Profile → Manage Subscription. Refunds are
            handled on a case-by-case basis per Razorpay policy.
          </Section>

          <Section title="7. Advertising">
            Free-tier users may see in-app advertisements delivered by Google AdMob. Ads are served
            according to Google’s policies and may collect device identifiers subject to your Android
            privacy settings.
          </Section>

          <Section title="8. Prohibited Conduct">
            You agree not to (a) upload content that is illegal, harmful, defamatory, sexually explicit,
            or infringing; (b) attempt to reverse-engineer, bypass paywalls or scrape the Service;
            (c) use the Service to impersonate any person; (d) share or resell AI-generated content in a
            way that misrepresents its origin.
          </Section>

          <Section title="9. Termination">
            We may suspend or terminate your access at any time for breach of these Terms. You may
            delete your account at any time from Profile → Settings → Delete Account.
          </Section>

          <Section title="10. Disclaimer &amp; Liability">
            The Service is provided on an “as-is” and “as-available” basis without warranties of any
            kind. To the maximum extent permitted by law, StyleBay is not liable for any indirect,
            incidental, special, consequential or punitive damages arising from your use of the Service.
            Our aggregate liability shall not exceed the amount you paid to StyleBay in the twelve
            months preceding the claim.
          </Section>

          <Section title="11. Changes to the Terms">
            We may update these Terms from time to time. Material changes will be notified in-app or by
            email at least 7 days before taking effect. Continued use after the effective date constitutes
            acceptance.
          </Section>

          <Section title="12. Governing Law">
            These Terms are governed by the laws of India. Any disputes shall be subject to the
            exclusive jurisdiction of the courts of Bengaluru, Karnataka.
          </Section>

          <Section title="12. Contact">
            Questions or notices: <a className="underline" href="mailto:contact@stylebay.co.in">contact@stylebay.co.in</a><br />
            Address: Gunour, Panna, MP, India
          </Section>

          <Section title="13. Grievance Officer (India IT Rules 2021)">
            In accordance with the Information Technology Act, 2000 and Rules made thereunder,
            the contact details of our Grievance Officer are:
            <br /><br />
            <b>Name:</b> Ram Soni<br />
            <b>Email:</b> <a className="underline" href="mailto:contact@stylebay.co.in">contact@stylebay.co.in</a><br />
            <b>Address:</b> Gunour, Panna, MP, India<br /><br />
            Complaints regarding privacy, security, objectionable content or breach of these
            Terms will be acknowledged within 24 hours and resolved within 15 days as required
            by law. File a grievance via <Link to="/grievance" className="underline">stylebay.co.in/grievance</Link>.
          </Section>
        </div>

        <p className="text-[10px] text-muted-foreground mt-12 uppercase tracking-[0.18em]">
          © 2026 StyleBay · <Link to="/privacy" className="underline">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-serif text-xl tracking-tight">{title}</h2>
      <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{children}</p>
    </section>
  );
}
